import { useState, useEffect } from 'react';
import { X, Star, ShoppingCart, Plus, Minus, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import CachedImage from './CachedImage';
import imageCache from '../utils/imageCache';

const ProductModal = ({ product, isOpen, onClose }) => {
  const { addToCart, isInCart, getItemQuantity, updateQuantity } = useCart();
  const [isAdding, setIsAdding] = useState(false);

  const inCart = isInCart(product?.id);
  const quantity = getItemQuantity(product?.id);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setImageLoaded(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleAddToCart = async () => {
    if (!product.price) return;
    
    setIsAdding(true);
    try {
      const cartItem = {
        id: product.id,
        name: product.title || product.name,
        price: product.price,
        image: cachedImageUrl || product.publicUrl || product.image,
        category: product.category,
        description: product.description
      };
      addToCart(cartItem);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleQuantityChange = (newQuantity) => {
    updateQuantity(product.id, newQuantity);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !product) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Product Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Image Section */}
            <div className="space-y-4">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative">
                <CachedImage
                  src={product.publicUrl || product.image}
                  alt={product.title || product.name}
                  className="w-full h-full object-contain p-4"
                  loading="eager"
                  preload={true}
                  placeholder={
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                    </div>
                  }
                  fallbackSrc="https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&h=400&fit=crop&crop=center"
                  fallbackType="electronics"
                />
                {product.featured && (
                  <div className="absolute top-4 left-4">
                    <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                      <Star className="w-4 h-4 mr-1" />
                      Featured
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              {/* Category */}
              <div className="text-sm text-primary-600 font-medium uppercase tracking-wide">
                {product.category}
              </div>

              {/* Title */}
              <h1 className="text-3xl font-bold text-gray-900">
                {product.title || product.name}
              </h1>

              {/* Stock Status */}
              <div className="flex items-center space-x-2">
                {product.inStock ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <Check className="w-4 h-4 mr-1" />
                    In Stock
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    Out of Stock
                  </span>
                )}
              </div>

              {/* Price */}
              {product.price && (
                <div className="text-4xl font-bold text-gray-900">
                  {typeof product.price === 'string' && product.price.startsWith('K')
                    ? product.price
                    : `K${product.price}`
                  }
                </div>
              )}

              {/* Description */}
              <div className="prose prose-gray max-w-none">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-600 leading-relaxed">
                  {product.description || 'No description available for this product.'}
                </p>
              </div>

              {/* Actions */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                {product.price && product.inStock && !inCart && (
                  <button
                    onClick={handleAddToCart}
                    disabled={isAdding}
                    className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-4 px-6 rounded-lg flex items-center justify-center transition-colors duration-200 disabled:opacity-50"
                  >
                    {isAdding ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Add to Cart
                      </>
                    )}
                  </button>
                )}

                {/* Quantity Controls */}
                {inCart && quantity > 0 && (
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                    <span className="text-lg text-primary-600 font-medium flex items-center">
                      <Check size={20} className="mr-2" />
                      In Cart: {quantity}
                    </span>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleQuantityChange(quantity - 1)}
                        className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="font-semibold text-lg min-w-[2rem] text-center">
                        {quantity}
                      </span>
                      <button
                        onClick={() => handleQuantityChange(quantity + 1)}
                        className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center hover:bg-primary-600 transition-colors"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {!product.inStock && (
                  <button
                    disabled
                    className="w-full bg-gray-300 text-gray-500 font-semibold py-4 px-6 rounded-lg cursor-not-allowed"
                  >
                    Out of Stock
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
